/**
 * TechHub > TechHub access (approved mockup 2026-10-23; ADR-0018/0022).
 * One card per company: the on/off switch (platform admins only) and who can use it.
 * Roles themselves are given in People & roles.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, type AdminTenant, type Member } from "../../lib/api";
import { useStore } from "../../lib/store";
import { byCoOrder } from "../../lib/brand";
import { Loading, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";

export function ClientHubAccessPage() {
  const { p, me, toast, reload } = useStore();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const load = async () => {
    try {
      const ts = (await api.adminTenants()).tenants.sort(byCoOrder);
      setTenants(ts);
      const on = ts.filter((t) => (t.modules ?? []).includes("clienthub"));
      const lists = await Promise.all(on.map((t) => api.members(t.code).then((r) => [t.code, r.members] as const, () => [t.code, []] as const)));
      setMembers(Object.fromEntries(lists));
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  if (err) return <div className="note bad">{err}</div>;
  if (!tenants) return <Loading />;
  const pa = me.user.is_platform_admin;

  const flip = async (t: AdminTenant, on: boolean) => {
    if (!on && !window.confirm(`Switch TechHub off for ${t.name}? It disappears for everyone there; nothing is deleted, and switching it back on brings everything back.`)) return;
    try {
      await api.setModules(t.code, { clienthub: on });
      toast(on ? `TechHub is on for ${t.name}. Give people TechHub roles in Settings > Team.` : `TechHub is off for ${t.name}.`);
      await load(); await reload();
    } catch (e) { toast((e as Error).message); }
  };

  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="clienthub" extra="Add-on to the Vault" />
        <h1 className="title">TechHub access</h1>
        <div className="lede">TechHub keeps each client's contacts, sites and notes (assets and agreements come next). It only exists inside DocVault, switched on per company.</div>
      </div></div>
      <div className="addons">
        {tenants.map((t) => {
          const on = (t.modules ?? []).includes("clienthub");
          const ms = members[t.code] ?? [];
          const n = (r: string) => ms.filter((m) => m.roles.includes(r as Member["roles"][number])).length;
          return (
            <section key={t.code} className="card addon">
              <div className="hd">
                <Tile code={t.code} />
                <div><b>{t.name}</b><div className="cap">{on ? "On" : "Off"}</div></div>
                <label className="switch" style={{ marginLeft: "auto" }} title={pa ? "" : "Only platform admins switch add-ons"}>
                  <input type="checkbox" checked={on} disabled={!pa} onChange={(e) => void flip(t, e.target.checked)} aria-label={`TechHub for ${t.name}`} />
                  <span />
                </label>
              </div>
              {on ? (
                <>
                  <div className="row"><span>TechHub viewers</span><b>{n("clienthub_viewer")}</b></div>
                  <div className="row"><span>TechHub editors</span><b>{n("clienthub_editor")}</b></div>
                  <div className="row"><span>Accounting (agreement values and signed copies)</span><b>{n("clienthub_accounting")}</b></div>
                  <div className="row"><span>Admins (full access, including admin-only details)</span><b>{n("admin")}</b></div>
                  <div className="row"><Link className="doclink" to={`/settings/people?co=${t.code}`}>Manage who can use it</Link><span /></div>
                </>
              ) : (
                <div className="cap">Turning it on adds the Vault | TechHub switch for the people you give TechHub roles. Turning it off hides it; nothing is deleted.</div>
              )}
            </section>
          );
        })}
      </div>
      <div className="foot-note">{pa ? "Only platform admins switch add-ons on or off." : "Only platform admins switch add-ons on or off. Ask one if your company needs TechHub."} TechHub's own lists (services, agreement types) are in the menu.</div>
    </div>
  );
}
